/**
 * The one knob that silences the whole flight mix when the sim stops.
 *
 * Pausing has to cut *everything* — not just the cues the frame loop would have
 * retriggered. A paused sim stops ticking, so any sound whose loudness is a
 * function of frozen state (the OPL2 engine voice's held key, the wind bed, the
 * gun crack the trigger was still firing the frame P was pressed) simply keeps
 * sounding at whatever level it last had. Muting per-source would mean teaching
 * every voice about pause; gating one node between the settings bus and the
 * destination costs one ramp and cannot miss a source.
 *
 * **A ramp, not `AudioContext.suspend()`.** Suspension is a whole-context state
 * change: it stops the `AudioWorklet` thread as well as the graph, its promise
 * settles a scheduling quantum later, and a rapid pause/unpause can land a
 * `resume()` before its `suspend()` has settled — the classic way to end up
 * paused-but-audible. A 20 ms linear ramp on a dedicated gain node is
 * synchronous to schedule, inaudible as a fade, and always ends where the last
 * call asked it to: each call cancels the previous automation and ramps from
 * wherever the previous one had got to, so a burst of toggles converges instead
 * of stacking.
 *
 * The `GateParam` structural type is `AudioParam` minus everything this does not
 * touch, which is what makes the schedule testable — `bun test` has no DOM and
 * so no real `AudioContext` (same reasoning as `settings.ts`'s `SettingsStore`).
 */

/**
 * Fade length in seconds. Long enough that a hard gain step never clicks, short
 * enough that "sound stops when I press P" still reads as instant — roughly one
 * frame at 60 Hz.
 */
export const PAUSE_FADE_S = 0.02;

/** The slice of `AudioParam` a pause gate uses. `cancelAndHoldAtTime` is optional: not every engine ships it. */
export interface GateParam {
  value: number;
  cancelScheduledValues(when: number): void;
  setValueAtTime(value: number, when: number): void;
  linearRampToValueAtTime(value: number, when: number): void;
  cancelAndHoldAtTime?(when: number): void;
}

export interface PauseGate {
  /** Idempotent: passing the state the gate is already in schedules nothing. */
  setPaused(paused: boolean): void;
  paused(): boolean;
}

/**
 * Pins the param at its current value and drops any automation still queued
 * behind it, so the ramp that follows starts from what is actually audible.
 *
 * `cancelAndHoldAtTime` does exactly this in one call. Without it, cancelling
 * alone would snap the param back to the value the last *event* set — the
 * audible click this whole module exists to avoid — so the fallback re-anchors
 * it by hand at the running value first.
 */
function holdAt(param: GateParam, when: number): void {
  if (param.cancelAndHoldAtTime) {
    param.cancelAndHoldAtTime(when);
    return;
  }
  const current = param.value;
  param.cancelScheduledValues(when);
  param.setValueAtTime(current, when);
}

/**
 * Drives `param` between 1 (running) and 0 (paused). `now` is the context clock
 * — a caller passes `() => context.currentTime`.
 */
export function createPauseGate(param: GateParam, now: () => number, fadeS: number = PAUSE_FADE_S): PauseGate {
  let paused = false;
  return {
    paused: () => paused,
    setPaused: (next) => {
      if (next === paused) return;
      paused = next;
      const when = now();
      holdAt(param, when);
      param.linearRampToValueAtTime(next ? 0 : 1, when + fadeS);
    },
  };
}
